import { Capacitor } from '@capacitor/core'
import { useAxios, useSubjectId } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useOutlet, useParams } from 'react-router-dom'
import { atom, useRecoilState } from 'recoil'
import styled from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { TabBar } from '@/@bothrs-ui/web/components/tab-bar/TabBar'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { mixpanel } from '@/mixpanel'
import { ScreenType, useScreenType } from '@/modules/shared/hooks/useScreenType'
import { scrollToId } from '@/modules/timeline/components/timeline/helpers/animation'
import { IntramurrosExpiredModal } from '@/modules/timeline/pages/intramuros/IntramurosExpiredModal.page'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { openUrl } from '@/utils/external-resources'
import { isIntramuros } from '@/utils/intramuros'
import { TaskList } from '../components/task-list/TaskList'

import type { Task, TaskResponse } from '../types/task.types'

export enum TaskCenterTab {
  Pending = 'pending',
  Resolved = 'resolved',
}

const TASKS_PER_PAGE = 20

type TaskState = {
  tasks: Task[]
  selectedTaskId?: string
  currentPage: number
  hasMore: boolean
  status: 'initial' | 'loading' | 'done'
}

export const taskStateAtom = atom<TaskState>({
  key: 'taskStateAtom',
  default: {
    tasks: [],
    currentPage: 0,
    hasMore: true,
    status: 'initial',
  },
})

export type TaskOutletContext = {
  /**
   * The active tab
   */
  activeTab: TaskCenterTab

  /**
   * Sets the active tab and reloads the task list
   * @param tab - The tab to set
   * @returns void
   */
  setActiveTab: (tab: TaskCenterTab) => void
  /**
   * Sets the selected task ID
   * @param taskId - The task ID to set
   * @returns void
   */
  updateTask: (taskId: string, task: Partial<Task>) => void

  /**
   * Refreshes the task list
   * @returns void
   */
  refreshTasks: () => void

  /**
   * Reloads the task list and selects a task
   * @param taskId - The task ID to select
   * @returns void
   */
  reloadAndSelectTask: (taskId: string) => void
}

export const TaskRootPage = () => {
  const isInitializedRef = useRef(false)
  const isMountedRef = useRef(true)
  const loadRequestIdRef = useRef(0)
  const location = useLocation()

  // Check if we should open a specific tab from navigation state
  const initialTab =
    (location.state as { tab?: string })?.tab === 'resolved' ? TaskCenterTab.Resolved : TaskCenterTab.Pending

  // State
  // ------------------------------------------------------------------------- /
  const [activeTab, setActiveTab] = useState<TaskCenterTab>(initialTab)
  const [IsIntraMurrosExpiredModal, setIsIntraMurrosExpiredModal] = useState<boolean>(false)

  const [taskState, setTaskState] = useRecoilState(taskStateAtom)

  // Hooks
  // ------------------------------------------------------------------------- /
  const outlet = useOutlet()
  const axios = useAxios()
  const navigate = useNavigate()
  const subjectId = useSubjectId()
  const screenType = useScreenType()
  const isMobile = useMediaQuery(breakpoints.toS)
  const isLoggedInForMandatee = useLoggedInForMandatee()

  const { id: routeParameterTaskId, key: routeParameterKey } = useParams()

  const isTaskRoot = location.pathname.endsWith('/task')
  const isIntramurosTaskRoot =
    isIntramuros && Boolean(routeParameterKey) && location.pathname.endsWith(`/intramuros/${routeParameterKey}`)

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (isInitializedRef.current || !screenType) return

    const initializeTaskList = async () => {
      if (isIntramuros) {
        mixpanel.track('task_intramuros_view', {
          is_intramuros: isIntramuros,
        })
      }
      // Prioritize route param task ID over state
      const taskIdToLoad = routeParameterTaskId || taskState.selectedTaskId

      if (taskIdToLoad) {
        // If we have a task ID (from route or state), fetch its details and set up the view
        try {
          const task = await axios.get<Task>(`/task/${taskIdToLoad}`)
          const status = task.data.status === 'pending' ? TaskCenterTab.Pending : TaskCenterTab.Resolved
          setActiveTab(status)
          await loadTasks({
            pageToFetch: 0,
            tab: status,
            taskIdToSelect: taskIdToLoad,
          })
        } catch (error) {
          console.error('Failed to fetch task:', error)
          setActiveTab(TaskCenterTab.Pending)
          await loadTasks({
            pageToFetch: 0,
            tab: TaskCenterTab.Pending,
          })
        }
      } else {
        // Otherwise just load tasks for the initial tab
        await loadTasks({
          pageToFetch: 0,
          tab: initialTab,
        })
      }
    }

    initializeTaskList()
    isInitializedRef.current = true
  }, [routeParameterTaskId, outlet, screenType])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      setTaskState((previous) => ({
        ...previous,
        status: 'initial',
        selectedTaskId: undefined,
        tasks: [],
      }))
      isInitializedRef.current = false
    }
  }, [])

  // Track intramuros view only once on mount
  useEffect(() => {
    if (isIntramuros) {
      mixpanel.track('task_intramuros_view', {
        is_intramuros: isIntramuros,
      })
    }
  }, [])

  // Methods
  // ------------------------------------------------------------------------- /
  /**
   * Handles first task selection logic for desktop
   * @param tasks - The tasks to select from
   * @param pageToFetch - Current page being fetched
   * @returns The selected task ID if any
   */
  const handleFirstTaskSelection = useCallback(
    (tasks: Task[], pageToFetch: number) => {
      let defaultSelectedTaskId: string | undefined

      // Select the first task on desktop if there are tasks and you are on the pending tab
      if (screenType !== ScreenType.Mobile && pageToFetch === 0 && tasks.length > 0) {
        defaultSelectedTaskId = tasks[0].id

        // Navigate to the task detail page
        // Important: only auto-redirect on web, never in the native app.
        const isTaskRootRoute = isIntramuros ? isIntramurosTaskRoot : isTaskRoot
        const shouldAutoRedirect = !Capacitor.isNativePlatform() && isMountedRef.current && isTaskRootRoute
        if (shouldAutoRedirect) {
          const path = isIntramuros ? `detail/${defaultSelectedTaskId}` : defaultSelectedTaskId
          navigate(path, { replace: true, relative: 'path' })
        }
      } else if (pageToFetch === 0) {
        // navigate('/task')
      }

      return defaultSelectedTaskId
    },
    [screenType, navigate, activeTab, isTaskRoot, isIntramurosTaskRoot, isIntramuros],
  )

  /**
   * Loads tasks for a given page and tab
   * @param pageToFetch - The page to fetch
   * @param tab - The tab to fetch tasks for
   * @param taskIdToSelect - Optional specific task ID to find and select
   * @returns The tasks
   */
  const loadTasks = useCallback(
    async ({
      pageToFetch,
      tab,
      limit = TASKS_PER_PAGE,
      taskIdToSelect,
      skipAutoSelect = false,
    }: {
      pageToFetch: number
      tab: TaskCenterTab
      limit?: number
      taskIdToSelect?: string
      skipAutoSelect?: boolean
    }) => {
      if (!screenType) return
      if (pageToFetch > 0 && taskState.status === 'loading') return

      if (pageToFetch === 0) loadRequestIdRef.current += 1
      const requestId = loadRequestIdRef.current

      setTaskState((previous) => ({ ...previous, status: 'loading' }))

      const statusQuery = tab === TaskCenterTab.Pending ? 'pending' : 'resolved'
      // In Intramuros mode, don't include subjectId in query - the proxy adds it from the token
      // In normal mode, subjectId is required from the authenticated user context
      const endpoint = isIntramuros
        ? `/task?status=${statusQuery}&page=${pageToFetch}&limit=${limit}`
        : `/task?status=${statusQuery}&page=${pageToFetch}&limit=${limit}&subjectId=${subjectId}`

      try {
        const response = await axios.get<TaskResponse>(endpoint)

        if (loadRequestIdRef.current !== requestId) return

        const { data: newTasks, page, total, limit } = response.data

        // If we're looking for a specific task, check if it's in this batch
        if (taskIdToSelect) {
          const foundTaskIndex = newTasks.findIndex((task) => task.id === taskIdToSelect)

          if (foundTaskIndex === -1) {
            // Task not found in this batch, check if we have more pages
            const hasMorePages = page * limit + newTasks.length < total

            if (hasMorePages) {
              // Accumulate tasks and recursively fetch next page
              setTaskState((previous) => ({
                ...previous,
                tasks: pageToFetch === 0 ? newTasks : [...previous.tasks, ...newTasks],
                selectedTaskId: taskIdToSelect,
                currentPage: page,
                hasMore: hasMorePages,
                status: 'done',
              }))

              return loadTasks({
                pageToFetch: page + 1,
                tab,
                taskIdToSelect,
                limit: 500,
              })
            } else {
              // No more pages and task not found, apply normal first task selection logic
              const defaultSelectedTaskId = handleFirstTaskSelection(newTasks, pageToFetch)

              setTaskState((previous) => ({
                ...previous,
                tasks: pageToFetch === 0 ? newTasks : [...previous.tasks, ...newTasks],
                currentPage: page,
                selectedTaskId: defaultSelectedTaskId,
                hasMore: false,
                status: 'done',
              }))

              // Scroll to the task on desktop
              if (screenType !== ScreenType.Mobile) {
                setTimeout(() => {
                  scrollToId(`task_${defaultSelectedTaskId}`)
                })
              }

              return newTasks
            }
          } else {
            // Update state with all tasks up to this point
            setTaskState((previous) => ({
              ...previous,
              tasks: pageToFetch === 0 ? newTasks : [...previous.tasks, ...newTasks],
              currentPage: page,
              selectedTaskId: taskIdToSelect,
              hasMore: page * limit + newTasks.length < total,
              status: 'done',
            }))

            // Navigate to the task (guard against unmounted component)
            if (isMountedRef.current) {
              const detailPath = isIntramuros ? `detail/${taskIdToSelect}` : taskIdToSelect
              navigate(detailPath, { relative: 'path' })
            }

            // Scroll to the task on desktop
            if (screenType !== ScreenType.Mobile) {
              setTimeout(() => {
                scrollToId(`task_${taskIdToSelect}`)
              })
            }

            return newTasks
          }
        }

        // Original logic for when no specific task is being searched for
        const defaultSelectedTaskId = skipAutoSelect ? undefined : handleFirstTaskSelection(newTasks, pageToFetch)

        setTaskState((previous) => ({
          ...previous,
          tasks: pageToFetch === 0 ? newTasks : [...previous.tasks, ...newTasks],
          currentPage: page,
          selectedTaskId: defaultSelectedTaskId,
          hasMore: page * limit + newTasks.length < total,
          status: 'done',
        }))

        // Scroll to the message on desktop
        if (defaultSelectedTaskId && screenType !== ScreenType.Mobile) {
          setTimeout(() => {
            scrollToId(`task_${defaultSelectedTaskId}`)
          })
        }

        return newTasks
      } catch (error) {
        if (loadRequestIdRef.current !== requestId) return

        console.error('Failed to fetch tasks:', error)
        mixpanel.track('task_error_state_shown', {
          origin,
          active_tab: activeTab,
          is_mandate: isLoggedInForMandatee,
          is_intramuros: isIntramuros,
        })
        if ((error as any)?.response?.status === 403 && isIntramuros) {
          setIsIntraMurrosExpiredModal(true)
          mixpanel.track('task_error_intramuros_expired', {
            is_mandate: isLoggedInForMandatee,
            is_intramuros: isIntramuros,
          })
        }
        setTaskState((previous) => ({
          ...previous,
          hasMore: false,
          status: 'done',
        }))
      }
    },
    [axios, subjectId, taskState.status, screenType, navigate, setTaskState, activeTab, handleFirstTaskSelection],
  )

  /**
   * Handles tab click
   * @param tab - The tab to switch to
   */
  const handleTabClick = useCallback(
    (tab: TaskCenterTab) => {
      mixpanel.track('task_tab_toggle', {
        origin,
        from_tab: activeTab,
        to_tab: tab,
        is_mandate: isLoggedInForMandatee,
        is_intramuros: isIntramuros,
      })

      setActiveTab(tab)
      setTaskState((previous) => ({
        ...previous,
        tasks: [],
        currentPage: 0,
        hasMore: true,
      }))
      if (routeParameterTaskId) {
        const segments = location.pathname.split('/')
        const removeCount = isIntramuros ? 2 : 1
        navigate(segments.slice(0, -removeCount).join('/') || '/')
      }

      loadTasks({ pageToFetch: 0, tab, skipAutoSelect: true })
    },
    [activeTab, loadTasks, setTaskState, navigate, routeParameterTaskId],
  )

  /**
   * Handles end reached
   */
  const handleEndReached = () => {
    if (taskState.hasMore && taskState.status === 'done') {
      loadTasks({
        pageToFetch: taskState.currentPage + 1,
        tab: activeTab,
      })
    }
  }

  /**
   * Handles task selection and navigation
   */
  const handleTaskSelected = (taskId: string) => {
    setTaskState((previous) => ({ ...previous, selectedTaskId: taskId }))

    const selectedTask = taskState.tasks.find((task) => task.id === taskId)

    if (
      selectedTask?.type === 'Remecare' &&
      selectedTask.status === 'pending' &&
      selectedTask.remecareUrl &&
      Capacitor.isNativePlatform()
    ) {
      openUrl(selectedTask.remecareUrl, {
        windowName: '_blank',
        forceDeeplinks: true,
      })
      return
    }

    const detailPath = isIntramuros ? `detail/${taskId}` : taskId
    navigate(detailPath, { relative: 'path' })
  }

  const updateTask = useCallback(
    (taskId: string, task: Partial<Task>) => {
      setTaskState((previous) => ({
        ...previous,
        tasks: previous.tasks.map((t) => (t.id === taskId ? ({ ...t, ...task } as Task) : t)),
      }))
    },
    [setTaskState],
  )

  const refreshTasks = useCallback(() => {
    loadTasks({ pageToFetch: 0, tab: activeTab })
  }, [loadTasks, activeTab])

  const reloadAndSelectTask = useCallback(
    async (taskId: string) => {
      setTaskState((previous) => ({
        ...previous,
        status: 'loading',
      }))

      const task = await axios.get<Task>(`/task/${taskId}`)
      const status = task.data.status === 'pending' ? TaskCenterTab.Pending : TaskCenterTab.Resolved

      setActiveTab(status)
      loadTasks({ pageToFetch: 0, tab: status, taskIdToSelect: taskId })
    },
    [loadTasks, axios, setActiveTab, setTaskState],
  )

  // Template variables
  // ------------------------------------------------------------------------- /
  const tabs = useMemo(
    () => [
      {
        id: TaskCenterTab.Pending,
        title: i18n._(globalDefinitions.task.pending),
        onClick: () => handleTabClick(TaskCenterTab.Pending),
      },
      {
        id: TaskCenterTab.Resolved,
        title: i18n._(globalDefinitions.task.resolved),
        onClick: () => handleTabClick(TaskCenterTab.Resolved),
      },
    ],
    [handleTabClick],
  )

  // Render
  // ------------------------------------------------------------------------- /
  const taskOutletContext: TaskOutletContext = {
    activeTab,
    setActiveTab,
    updateTask,
    refreshTasks,
    reloadAndSelectTask,
  }

  if (isTaskRoot || !(Capacitor.isNativePlatform() || isMobile)) {
    return (
      <ContentPageLayout
        noPadding
        animationkey={isIntramuros ? `intramuros-task-root-${routeParameterKey ?? 'unknown'}` : 'task-root'}
        header={
          <Suspense fallback={null}>
            <NativePlatform>
              <StMobileHeader>
                <AppHeader
                  title={i18n._(globalDefinitions.task.title)}
                  enableNotifications
                  profileButtonEnabled
                  centered={false}
                />
              </StMobileHeader>
            </NativePlatform>
          </Suspense>
        }
      >
        <NativePlatform>
          <TabBar variant='underline' activeTabId={activeTab} tabs={tabs} />
          <TaskList
            tasks={taskState.tasks}
            selectedTaskId={taskState.selectedTaskId}
            activeTab={activeTab}
            hasMore={taskState.hasMore}
            isLoading={taskState.status !== 'done'}
            onEndReached={handleEndReached}
            onTaskSelected={handleTaskSelected}
          />
        </NativePlatform>
        {/* Web platform */}
        <WebPlatform>
          <WebContent>
            <StDesktopTaskList>
              <StWebTabbar activeTabId={activeTab} tabs={tabs} variant='underline' />

              <TaskList
                tasks={taskState.tasks}
                selectedTaskId={taskState.selectedTaskId}
                activeTab={activeTab}
                hasMore={taskState.hasMore}
                isLoading={taskState.status !== 'done'}
                onEndReached={handleEndReached}
                onTaskSelected={handleTaskSelected}
              />
            </StDesktopTaskList>

            {/* Rigth side */}
            <StSelectedContent>
              {routeParameterTaskId ? (
                <Outlet context={taskOutletContext} />
              ) : taskState.status !== 'loading' && taskState.tasks.length > 0 ? (
                <StNoTaskSelectedContainer>
                  <StNoTaskSelectedImage src='/img/no-item-selected.png' alt='No task selected' />
                  <H3 style={{ fontSize: '14px' }}>
                    {t({
                      id: 'task-detail.noTaskSelected',
                      message: 'Selecteer een taak links in de lijst',
                    })}
                  </H3>
                </StNoTaskSelectedContainer>
              ) : null}
            </StSelectedContent>
          </WebContent>
        </WebPlatform>

        <IntramurrosExpiredModal isVisible={IsIntraMurrosExpiredModal} />
      </ContentPageLayout>
    )
  }

  return <Outlet context={taskOutletContext} />
}

//  ------------------------------------------------  Desktop content ------------------------------------------------ /
const StDesktopTaskList = styled(FlexColumn)`
  width: 100%;
  max-width: 50%;
  height: 100%;
  border-right: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  flex: 1;
  flex-shrink: 1;
`

const StWebTabbar = styled(TabBar)`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space5};
  display: sticky;
  top: 0;

  @media ${breakpoints.s} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space6} - 16px);
  }
  @media ${breakpoints.m} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space12} - 16px);
  }

  @media ${breakpoints.l} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space20} - 16px);
  }
`

const StSelectedContent = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  flex: 1;
  flex-grow: 1;
  flex-shrink: 0;
`

const StNoTaskSelectedContainer = styled(FlexColumn)`
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StNoTaskSelectedImage = styled.img`
  max-width: 220px;
  width: 50%;
  opacity: 0.5;
`

const WebContent = styled(FlexRow)`
  width: 100%;
  height: 100%;
  flex: 1;
`

//  ------------------------------------------------  Mobile components ------------------------------------------------ /
const StMobileHeader = styled.div`
  ${ContentPagePaddingCSS}
  padding-bottom: 0;
`
